import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CORRECTIONS, MODELS } from '@/data/catalogue';
import { canonical } from '@/lib/site';

export default function Corrections() {
  const [params] = useSearchParams();
  const [model, setModel] = useState(params.get('model') ?? '');
  const [what, setWhat] = useState('');
  const [source, setSource] = useState('');
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    const m = MODELS.find((x) => x.id === model);
    return [`Correction report — ${m ? `${m.brand} ${m.name} (${m.id})` : 'general'}`, '', `What is wrong: ${what.trim()}`, `Source: ${source.trim() || '(none given)'}`, `Page: ${window.location.origin}${m ? `/products/${m.id}` : ''}`].join('\n');
  }, [model, what, source]);
  const valid = what.trim().length >= 10 && what.length <= 2000 && source.length <= 500;

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt('Copy this report:', text); }
  };

  return (
    <>
      <Helmet>
        <title>Corrections log and error reports</title>
        <meta name="description" content="Every material correction to this site, and how to report an error." />
        <link rel="canonical" href={canonical('/corrections')} />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <h1 className="text-3xl font-bold text-foreground">Corrections</h1>

        <section className="mt-8">
          <h2 className="text-xl font-bold text-foreground mb-3">Report an error</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Prepare a correction report to copy and share with the site owner. This page does not submit the report. Include a source link when possible.
          </p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="corr-model">Product (optional)</Label>
              <select id="corr-model" value={model} onChange={(e) => setModel(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">General / calculator / article</option>
                {MODELS.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="corr-what">What's wrong? (10–2000 characters)</Label>
              <Textarea id="corr-what" value={what} maxLength={2000} onChange={(e) => setWhat(e.target.value)} rows={4} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="corr-src">Source link (optional)</Label>
              <Input id="corr-src" value={source} maxLength={500} onChange={(e) => setSource(e.target.value)} placeholder="https://…" />
            </div>
            <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 text-xs text-foreground" aria-label="Report preview">{text}</pre>
            <Button onClick={copy} disabled={!valid}>{copied ? 'Copied' : 'Copy report'}</Button>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold text-foreground mb-3">Correction history</h2>
          <ol className="space-y-3">
            {CORRECTIONS.map((c) => (
              <li key={c.summary} className="border-l-2 border-border pl-4">
                <p className="text-xs text-muted-foreground">{c.date}</p>
                <p className="text-sm text-foreground">{c.summary}</p>
              </li>
            ))}
          </ol>
          <p className="text-sm text-muted-foreground mt-6">Withdrawn product records keep their old addresses with an explanation — see the <Link to="/products" className="text-primary underline">catalogue</Link>.</p>
        </section>
      </main>
      <Footer />
    </>
  );
}
